import { concat } from '@ember/helper';
import t from 'ember-intl/helpers/t';
import CrudJournalEventBaseComponent from 'parog-web/components/forc/abstract/crud-journal-event-based-component';
import Provider from 'parog-web/components/forc/provider';
import journalEventRetrievalStrategies from 'parog-web/components/forc/utils/journal-event-retrieval-strategies';
import AccordionBlock from 'parog-web/components/medical-history/accordion-block';
import MrcReplacementJournalEvent from 'parog-web/models/mrc-replacement-journal-event';

export default class MrcReplacementEditionFormComponent extends CrudJournalEventBaseComponent<MrcReplacementJournalEvent> {
  static override forcDisplayName =
    'Préparation au traitement de suppléance rénale';
  static override fullComponentId = 'replacement/edition-form';
  static override journalEventClass = MrcReplacementJournalEvent;

  chosenHemodialysisType = [
    'hemodialysis-in-hospital-center',
    'hemodialysis-in-a-medicalized-dialysis-unit',
    'hemodialysis-in-a-self-dialysis-unit',
    'hemodialysis-at-home'
  ];
  plannedReplacementTherapy = [
    'hemodialysis-in-hospital-center',
    'hemodialysis-in-a-medicalized-dialysis-unit',
    'hemodialysis-in-a-self-dialysis-unit',
    'hemodialysis-at-home',
    'continuous-ambulatory-peritoneal-dialysis',
    'automated-peritoneal-dialysis',
    'conservative-treatment',
    'not-yet-defined'
  ];
  plannedVascularAccess = [
    'arteriovenous-fistula',
    'central-vein-catheter-tunneled-catheter',
    'graft',
    'unknown'
  ];
  override retrieveJournalEvent =
    journalEventRetrievalStrategies.sourceJournalEvent;
  smoking = ['active-smoker', 'non-smoker', 'former-smoker', 'unknown'];
  smokingCessation = ['not-planned', 'planned', 'in-progress'];
  typeOfPeritonealDialysis = [
    'continuous-ambulatory-peritoneal-dialysis',
    'automated-peritoneal-dialysis'
  ];

  updateChosenHemodialysis = () => {
    const chosenHemodialysis = this.changeset.get('chosenHemodialysisType');

    this.changeset.set('plannedReplacementTherapy', chosenHemodialysis);
    this.changeset.set('typeOfPeritonealDialysis', null);
  };

  updateDialysisTypes = () => {
    const dialysisType = this.changeset.get('plannedReplacementTherapy');

    if (dialysisType === 'not-yet-defined') {
      this.changeset.set('chosenHemodialysisType', null);
      this.changeset.set('typeOfPeritonealDialysis', null);
    }

    if (this.chosenHemodialysisType.includes(dialysisType)) {
      this.changeset.set('chosenHemodialysisType', dialysisType);
      this.changeset.set('typeOfPeritonealDialysis', null);
    } else if (this.typeOfPeritonealDialysis.includes(dialysisType)) {
      this.changeset.set('typeOfPeritonealDialysis', dialysisType);
      this.changeset.set('chosenHemodialysisType', null);
    }
  };

  updatePeritonealDialysis = () => {
    const peritonealDialysis = this.changeset.get('typeOfPeritonealDialysis');

    this.changeset.set('plannedReplacementTherapy', peritonealDialysis);
    this.changeset.set('chosenHemodialysisType', null);
  };

  <template>
    <Provider @forcComponent={{this}} @patient={{@patient}}>
      <:content as |Form|>

        <div class='field'>
          <Form.Dropdown
            @labelName='Traitement de suppléance envisagé'
            @propertyName='plannedReplacementTherapy'
            @onChangesetUpdate={{this.updateDialysisTypes}}
            as |dropdown|
          >
            {{#each this.plannedReplacementTherapy as |key|}}
              <dropdown.Item
                @key={{key}}
                @value={{t
                  (concat 'replacement.planned-replacement-therapy.' key)
                }}
              />
            {{/each}}
          </Form.Dropdown>
        </div>
        <div class='field'>
          <Form.Date
            @labelName='Date de démarrage envisagée'
            @propertyName='plannedStartDate'
          />
        </div>
        <div class='field'>
          <Form.AlignedFieldsRadioGroup
            @title="Rencontre d'information du patient réalisée"
            @propertyName='patientInformationMeetingHeld'
            as |Radio|
          >
            <Radio.FieldRadio @value={{true}} @label='Oui' />
            <Radio.FieldRadio @value={{false}} @label='Non' />
          </Form.AlignedFieldsRadioGroup>
        </div>
        <div class='field'>
          <Form.Date
            @labelName='Date de la rencontre'
            @propertyName='meetingDate'
          />
        </div>

        <AccordionBlock
          @title='Suivi médico-social du patient'
          @isActive={{true}}
        >
          <div class='field'>
            <Form.TextArea
              @labelName='Mode de vie du patient'
              @propertyName='patientLifestyle'
              @placeholder='Ajouter les informations nécessaires au choix du traitement de suppléance du patient'
              @rows={{1}}
            />
          </div>

          <div class='two fields'>
            <div class='field'>
              <Form.Dropdown
                @labelName='Tabac'
                @propertyName='smoking'
                as |dropdown|
              >
                {{#each this.smoking as |key|}}
                  <dropdown.Item
                    @key={{key}}
                    @value={{t (concat 'replacement.smoking.' key)}}
                  />
                {{/each}}
              </Form.Dropdown>
            </div>
            <div class='field'>
              <Form.Dropdown
                @labelName='Sevrage tabagique'
                @propertyName='smokingCessation'
                as |dropdown|
              >
                {{#each this.smokingCessation as |key|}}
                  <dropdown.Item
                    @key={{key}}
                    @value={{t (concat 'replacement.smoking-cessation.' key)}}
                  />
                {{/each}}
              </Form.Dropdown>
            </div>
          </div>
        </AccordionBlock>

        <AccordionBlock
          @title="Mise en place de l'hémodialyse"
          @isActive={{true}}
        >
          <div class='two fields'>
            <div class='field'>
              <Form.Dropdown
                @labelName="Type d'hémodialyse choisi"
                @propertyName='chosenHemodialysisType'
                @onChangesetUpdate={{this.updateChosenHemodialysis}}
                as |dropdown|
              >
                {{#each this.chosenHemodialysisType as |key|}}
                  <dropdown.Item
                    @key={{key}}
                    @value={{t
                      (concat 'replacement.chosen-hemodialysis-type.' key)
                    }}
                  />
                {{/each}}
              </Form.Dropdown>
            </div>
            <div class='field'>
              <Form.Date
                @labelName='Date de démarrage de la dialyse'
                @propertyName='dialysisStartDateHaemodialysis'
              />
            </div>
          </div>
          <div class='two fields'>
            <div class='field'>
              <Form.Dropdown
                @labelName='Abord vasculaire envisagé'
                @propertyName='plannedVascularAccess'
                as |dropdown|
              >
                {{#each this.plannedVascularAccess as |key|}}
                  <dropdown.Item
                    @key={{key}}
                    @value={{t
                      (concat 'replacement.planned-vascular-access.' key)
                    }}
                  />
                {{/each}}
              </Form.Dropdown>
            </div>
            <div class='field'>
              <Form.Date
                @labelName="Date de la création de l'abord"
                @propertyName='vascularAccessCreationDate'
              />
            </div>
          </div>
          <div class='field'>
            <Form.AlignedFieldsRadioGroup
              @title='Abord vasculaire fonctionnel'
              @propertyName='functionalVascularAccess'
              as |Radio|
            >
              <Radio.FieldRadio @value={{true}} @label='Oui' />
              <Radio.FieldRadio @value={{false}} @label='Non' />
            </Form.AlignedFieldsRadioGroup>
          </div>
          <div class='field'>
            <Form.Input
              @propertyName='plannedDialysisCenter'
              @labelName='Centre de dialyse prévu'
            />
          </div>
          <div class='field'>
            <Form.Input
              @propertyName='dialysisSoftwareUsed'
              @labelName='Logiciel de dialyse prévu'
            />
          </div>
        </AccordionBlock>

        <AccordionBlock
          @title='Mise en place de la dialyse péritonéale'
          @isActive={{true}}
        >
          <div class='two fields'>
            <div class='field'>
              <Form.Dropdown
                @labelName='Type de dialyse péritonéale choisi'
                @propertyName='typeOfPeritonealDialysis'
                @onChangesetUpdate={{this.updatePeritonealDialysis}}
                as |dropdown|
              >
                {{#each this.typeOfPeritonealDialysis as |key|}}
                  <dropdown.Item
                    @key={{key}}
                    @value={{t
                      (concat 'replacement.type-of-peritoneal-dialysis.' key)
                    }}
                  />
                {{/each}}
              </Form.Dropdown>
            </div>
            <div class='field'>
              <Form.Date
                @labelName='Date de démarrage de la dialyse'
                @propertyName='dialysisStartDatePeritoneal'
              />
            </div>
          </div>
          <div class='field'>
            <Form.AlignedFieldsRadioGroup
              @title='Rencontre avec le chirurgien'
              @propertyName='meetingWithSurgeon'
              as |Radio|
            >
              <Radio.FieldRadio @value={{true}} @label='Oui' />
              <Radio.FieldRadio @value={{false}} @label='Non' />
            </Form.AlignedFieldsRadioGroup>
          </div>
          <div class='field'>
            <Form.Date
              @labelName='Date de pose du cathéter péritonéal'
              @propertyName='peritonealCatheterPlacementDate'
            />
          </div>
        </AccordionBlock>
      </:content>
    </Provider>
  </template>
}
